/**
 * StreamNotificationFooter - Shows like and join notifications with queue (Webplay version)
 * Displays "Username liked x5" or "Username joined" below chat messages
 */
import React, { useState, useEffect, useRef } from 'react';

interface NotificationEvent {
  id: string;
  type: 'like' | 'join';
  username: string;
  count?: number; // For likes
  timestamp: number;
}

interface StreamNotificationFooterProps {
  isDarkMode?: boolean;
}

export const StreamNotificationFooter: React.FC<StreamNotificationFooterProps> = ({
  isDarkMode = true
}) => {
  const [queue, setQueue] = useState<NotificationEvent[]>([]);
  const [currentNotification, setCurrentNotification] = useState<NotificationEvent | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Expose addNotification method globally
  useEffect(() => {
    if (!(window as any).streamNotificationQueue) {
      (window as any).streamNotificationQueue = {
        addLike: (username: string, count: number) => {
          addNotification({ type: 'like', username, count });
        },
        addJoin: (username: string) => {
          addNotification({ type: 'join', username });
        }
      };
    }

    return () => {
      if ((window as any).streamNotificationQueue) {
        delete (window as any).streamNotificationQueue;
      }
    };
  }, []);

  const addNotification = (event: Omit<NotificationEvent, 'id' | 'timestamp'>) => {
    const newEvent: NotificationEvent = {
      ...event,
      id: `${event.type}-${Date.now()}-${Math.random()}`,
      timestamp: Date.now()
    };

    setQueue(prev => [...prev, newEvent]);
  };

  // Process queue
  useEffect(() => {
    if (isProcessing || queue.length === 0) return;

    const nextEvent = queue[0];
    
    // Calculate display duration BEFORE removing from queue
    // Check if there will be more items after we remove this one
    const hasMoreInQueue = queue.length > 1;
    let displayDuration: number;

    if (nextEvent.type === 'like') {
      displayDuration = hasMoreInQueue ? 1000 : 3000; // 1s min, 3s if empty
    } else {
      displayDuration = hasMoreInQueue ? 500 : 3000; // 0.5s min, 3s if empty
    }

    // Start processing
    setIsProcessing(true);
    setCurrentNotification(nextEvent);
    
    // Remove from queue after a tick
    setTimeout(() => setQueue(prev => prev.slice(1)), 0);

    // Schedule next item
    const timeout = setTimeout(() => {
      setCurrentNotification(null);
      setIsProcessing(false);
    }, displayDuration);
    
    timeoutRef.current = timeout;
  }, [queue, isProcessing]);
  
  // Cleanup only on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const { type, username, count } = currentNotification || {};

  return (
    <div
      className={`
        w-full flex flex-row items-center justify-between
        px-3 py-2 border-t transition-opacity duration-200
        ${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}
        ${currentNotification ? 'opacity-100' : 'opacity-0'}
      `}
      style={{ minHeight: '48px' }}
    >
      {currentNotification && (
        <>
          <p className={`text-sm ${isDarkMode ? 'text-white' : 'text-black'}`}>
            <strong>{username}</strong>
            {type === 'like' ? (
              <>
                {' liked'}
                {count && count > 1 && (
                  <strong className="text-base"> x{count}</strong>
                )}
              </>
            ) : (
              ' joined'
            )}
          </p>
          <span className="text-lg">
            {type === 'like' ? '❤️' : '👋'}
          </span>
        </>
      )}
    </div>
  );
};
